import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {httpDeleteUserGroup} from '@/features/user-groups/http/user-groups.http.ts'
import {captureException} from '@sentry/react'
import {z} from 'zod'
import {QUERY_KEYS} from '@/queryClient.ts'
import {UserGroup} from '@/features/user-groups/types.ts'
import {PaginationResponse} from '@/types'

// Accept either a boolean success object or just a string (or any empty value)
const ResponseShape = z.union([z.object({success: z.boolean()}), z.string(), z.any()])
type ResponseShape = z.infer<typeof ResponseShape>

// Type for the data structure returned by the query
type UserGroupsQueryData = {
    data: UserGroup[]
    meta: PaginationResponse
}

// Type for the mutation context
type MutationContext = {
    previousData: unknown
}

export const useDeleteUserGroup = (
    options?: Omit<UseMutationOptions<ResponseShape, Error, string, MutationContext>, 'mutationFn'>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (groupId: string) =>
            parseAxiosPromise({
                axiosPromise: httpDeleteUserGroup({urlParams: {groupId}}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        onMutate: async groupId => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.USER_GROUPS]})

            // Snapshot the previous state
            const previousData = queryClient.getQueryData([QUERY_KEYS.USER_GROUPS])

            // Apply optimistic update - filter out the deleted group
            queryClient.setQueryData<UserGroupsQueryData>([QUERY_KEYS.USER_GROUPS], old => {
                if (!old) return old

                const filteredGroups = old.data.filter(group => group.id !== groupId)

                // Update total count in pagination data
                const updatedMeta = {
                    ...old.meta,
                    total: Math.max(0, old.meta.total - 1)
                }

                return {
                    ...old,
                    data: filteredGroups,
                    meta: updatedMeta
                }
            })

            return {previousData}
        },
        onError: (_, __, context) => {
            // Restore the previous state if there was an error
            if (context?.previousData) {
                queryClient.setQueryData([QUERY_KEYS.USER_GROUPS], context.previousData)
            }
        },
        ...options
    })
}
